﻿<script setup lang="ts">
    import { onMounted, reactive } from 'vue';
    import { useRouter } from 'vue-router';
    import {
        getAccount,
        Account,
        saveAccount,
        getAccountUsers,
        User,
        getSmtpServers,
        SmtpServer,
        getAccountStatusClass,
        getAccountEmailsPendingValidation,
        EmailValidation,
        validateEmail,
        markAccountForDeletion,
        getContactDeleteCount,
        deleteContactsByType,
        getClientCompanies,
        type ClientCompaniesListResult,
    } from '@cwd/Views/BackOffice/Account/Account.access';
    import Tabs, { tab } from '@shared/components/Tabs.vue';
    import Loading from '@shared/components/Loading.vue';
    import CopyButton from '@shared/components/CopyButton.vue';
    import Form from '@cwd/shared/Components/Form/Form.vue';
    import { FormProps } from '@cwd/shared/components/Form/Form';
    import DomainWrapper from './DomainWrapper.vue';
    import { formatDate } from '@cwd/Shared/utils/formatDateTime';
    import { vConfirm } from '@shared/directives/VConfirm';
    import { bsAlert } from '@shared/components/BsAlerts';
    import List from '@shared/components/List.vue';
    import { ClientCompany } from '@shared/Root/Context.access';
    import { $Resources } from '@cwd/lib/main';
    import BSFormSearch from '@shared/components/BSFormSearch.vue';
    import BSFormInputText from '@shared/components/BSFormInputText.vue';
    import DeleteAccount from './DeleteAccount.vue';
    const router = useRouter();

    const props = defineProps<{
        accountId: string;
    }>();

    const views = {
        currentAccount: 0,
        companiesList: 1,
    };

    const state = reactive<{
        account: Account | null;
        companies: ClientCompaniesListResult | null;
        currentView: number;
        isLoading: boolean;
        isValidating: boolean;
        isLoadingContacts: boolean;
        formReady: boolean;
        settingsForm: FormProps;
        senderSettingsForm: FormProps;
        smtpServers: SmtpServer[];
        users: User[];
        emailValidations: EmailValidation[];
        contactDeleteType: string;
        contactDeleteCount: number | null;
        retention: number;
    }>({
        account: null,
        companies: null,
        currentView: views.currentAccount,
        isLoading: false,
        isValidating: false,
        isLoadingContacts: false,
        formReady: false,
        settingsForm: {
            disableSubmit: false,
            alert: null,
            data: {},
        },
        senderSettingsForm: {
            disableSubmit: false,
            alert: null,
            data: {},
        },
        smtpServers: [],
        users: [],
        emailValidations: [],
        contactDeleteType: 'deleted',
        contactDeleteCount: null,
        retention: 180,
    });

    onMounted(async () => {
        await get();
    });

    async function get(): Promise<void> {
        state.isLoading = true;

        await Promise.all([getAccountData(), getUsers(), getSmtp()]);

        setupForms();

        state.isLoading = false;

        await getEmailsPendingValidation();
    }

    async function getAccountData(): Promise<void> {
        await getAccount(props.accountId)
            .then((data) => {
                state.account = data;
            })
            .catch(() => {
                bsAlert({ title: 'Error', content: 'Failed to get account', type: 'danger' });
            });
    }

    async function getClientCompaniesData(): Promise<void> {
        if (state.isLoading) return;
        state.isLoading = true;

        await getClientCompanies().then((data) => {
            state.companies = data;
        });

        state.isLoading = false;
    }

    async function getUsers(): Promise<void> {
        await getAccountUsers(props.accountId).then((data) => {
            state.users = data;
        });
    }

    async function getSmtp(): Promise<void> {
        await getSmtpServers().then((data) => {
            state.smtpServers = data;
        });
    }

    async function getEmailsPendingValidation(): Promise<void> {
        await getAccountEmailsPendingValidation(props.accountId).then((data) => {
            state.emailValidations = data;
        });
    }

    async function validate(validation: EmailValidation): Promise<void> {
        if (state.isValidating === true) return;

        state.isValidating = true;

        await validateEmail(validation)
            .then((data) => {
                validation.IsValidated = true;
            })
            .catch(() => {})
            .finally(() => (state.isValidating = false));
    }

    async function saveSettings(event: Event, formData: any) {
        if (state.isLoading === true || !state.account) return;

        state.isLoading = true;

        Object.assign(state.account, formData);

        await saveAccount(state.account)
            .then((data) => {
                state.account = data;

                bsAlert({ title: 'Saved', content: 'Account saved', type: 'success' });
            })
            .catch(() => {
                bsAlert({ title: 'Error', content: 'Something went wrong', type: 'danger' });
            })
            .finally(() => (state.isLoading = false));
    }

    async function saveSenderSettings(event: Event, formData: any) {
        if (state.isLoading === true || !state.account) return;

        state.isLoading = true;

        Object.assign(state.account, formData);

        await saveAccount(state.account)
            .then((data) => {
                state.account = data;

                bsAlert({ title: 'Saved', content: 'Account saved', type: 'success' });
            })
            .catch(() => {
                bsAlert({ title: 'Error', content: 'Something went wrong', type: 'danger' });
            })
            .finally(() => (state.isLoading = false));
    }

    async function countDeletion() {
        if (state.isLoadingContacts === true) return;

        state.contactDeleteCount = null;
        state.isLoadingContacts = true;

        await getContactDeleteCount(props.accountId, state.contactDeleteType)
            .then((data) => {
                state.contactDeleteCount = data;
            })
            .catch(() => {})
            .finally(() => (state.isLoadingContacts = false));
    }

    async function deleteContacts() {
        if (state.isLoadingContacts === true) return;

        state.isLoadingContacts = true;

        await deleteContactsByType(props.accountId, state.contactDeleteType)
            .then((data) => {
                state.contactDeleteCount = null;
            })
            .catch(() => {})
            .finally(() => (state.isLoadingContacts = false));
    }

    function setupForms(): void {
        setupSettingsFormData();
        setupSenderSettingsFormData();

        state.formReady = true;
    }

    function setupSettingsFormData(): void {
        state.settingsForm.data = {
            Name: {
                modelValue: state.account?.Name,
                group: 'Account',
                required: true,
                input: {
                    label: 'Name',
                },
            },
            Status: {
                modelValue: state.account?.Status,
                group: 'Account',
                select: {
                    label: 'Status',
                    options: [
                        { value: '2', label: 'Active' },
                        { value: '3', label: 'Cancelled' },
                        { value: '4', label: 'Pending Validate' },
                    ],
                    readonly: true,
                },
            },
            Start: {
                modelValue: state.account?.Start,
                group: 'Active period',
                required: false,
                input: {
                    label: 'Start',
                    type: 'date',
                },
            },
            End: {
                modelValue: state.account?.End,
                group: 'Active period',
                required: false,
                input: {
                    label: 'End',
                    type: 'date',
                },
            },
            EnabledAuthenticationMethods: {
                modelValue: state.account?.EnabledAuthenticationMethods,
                group: 'Auth',
                select: {
                    label: 'Enabled authentication methods',
                    options: [
                        { value: '1', label: 'Password' },
                        { value: '2', label: 'BankId' },
                        { value: '4', label: 'PasswordAndGoogleAuthenticator' },
                        { value: '8', label: 'SSO (OpenId)' },
                    ],
                    readonly: true,
                },
            },
            DefaultAccessControl: {
                modelValue: state.account?.DefaultAccessControl,
                group: 'Auth',
                select: {
                    label: 'ACL default',
                    options: [
                        { value: '0', label: 'None' },
                        { value: '1', label: 'CreatingUser' },
                        { value: '3', label: 'CreatingUserGroups' },
                    ],
                    readonly: true,
                },
            },
            ContactPerson: {
                group: 'Account',
                modelValue: state.account?.ContactPerson,
                input: {
                    label: 'Contact person',
                },
            },
            Comments: {
                group: 'Other',
                modelValue: state.account?.Comments,
                textArea: {
                    label: 'Comments',
                },
            },
        };
    }

    function setupSenderSettingsFormData(): void {
        let smtpServerOptions = state.smtpServers.map((server) => {
            return { value: server.SmtpServerId, label: server.Name };
        });

        state.senderSettingsForm.data = {
            SmtpServerId: {
                modelValue: state.account?.SmtpServerId,
                group: 'SMTP',
                required: true,
                select: {
                    label: 'Delivery smtp server',
                    options: smtpServerOptions,
                    readonly: true,
                },
            },
            SystemSmtpServerId: {
                modelValue: state.account?.SystemSmtpServerId,
                group: 'SMTP',
                select: {
                    label: 'Transactional smtp server',
                    options: smtpServerOptions,
                    readonly: true,
                },
            },
            UseSmtpServerDkimOverrides: {
                modelValue: state.account?.UseSmtpServerDkimOverrides,
                required: false,
                checkbox: {
                    label: 'Use SmtpServer Dkim Overrides',
                },
            },
            SendWithVerp: {
                modelValue: state.account?.SendWithVerp,
                required: false,
                checkbox: {
                    label: 'Send with VERP (Variable envelope return path)',
                },
            },
            DefaultReplyToAddress: {
                modelValue: state.account?.DefaultReplyToAddress,
                required: true,
                input: {
                    label: 'Default Reply-to address (copied to all new emails)',
                },
            },
            DefaultFromName: {
                modelValue: state.account?.DefaultFromName,
                required: true,
                input: {
                    label: 'Default From Name (copied to all new emails)',
                },
            },
            DefaultFromAddress: {
                modelValue: state.account?.DefaultFromAddress,
                required: true,
                input: {
                    label: 'Default From address (bounces go here, used if no specific from address given on an email)',
                },
            },
            SmsAnswerCode: {
                modelValue: state.account?.SmsAnswerCode,
                required: true,
                input: {
                    label: 'Sms answer code',
                },
            },
        };
    }

    const listCompanies = () => {
        state.currentView = views.companiesList;
        !state.companies && getClientCompaniesData();
    };

    async function updateCurrentCompany(data: ClientCompany) {
        if (state.isLoading === true || !state.account) return;
        state.isLoading = true;

        state.account!.ClientCompany.Name = data.Name;
        state.account!.ClientCompany.ClientCompanyId = data.ClientCompanyId;

        await saveAccount(state.account)
            .then((data) => {
                state.account = data;
                bsAlert({ title: 'Saved', content: 'Account saved', type: 'success' });
            })
            .catch(() => {
                bsAlert({ title: 'Error', content: 'Something went wrong', type: 'danger' });
            })
            .finally(() => {
                state.isLoading = false;
                state.currentView = views.currentAccount;
            });
    }
</script>

<template>
    <p class="fw-600 m-lg-t">
        <router-link v-show="state.currentView === views.currentAccount" :to="{ name: 'AccountList' }">
            <span class="glyphicon glyphicon-chevron-left c-light" aria-hidden="true"></span>
            Accounts
        </router-link>
        <a href="#" role="button" v-show="state.currentView === views.companiesList" @click="state.currentView = views.currentAccount">
            <span class="glyphicon glyphicon-chevron-left c-light m-xs-r" aria-hidden="true"></span>
            {{ state.account?.Name }}
        </a>
    </p>

    <Loading :show="state.isLoading" loadingStyle="progress-bar"></Loading>

    <template v-if="state.account != null">
        <div v-show="state.currentView === views.currentAccount">
            <h2 class="h3 m-0-t">
                <i class="fa fa-square" aria-hidden="true" :class="getAccountStatusClass(state.account)"></i>
                {{ state.account.Name }}
                <small class="m-xs-l">
                    <router-link :to="{ name: 'SubscriptionEdit', params: { serviceSubscriptionId: state.account.ServiceSubscriptionId } }">
                        {{ state.account.ClientCompany.Name }}
                    </router-link>
                </small>
            </h2>

            <p>
                <a class="btn btn-default btn-sm" :href="'/Account/' + props.accountId">Sign in to this account</a>
                <CopyButton class="m-xs-l" :textToCopy="props.accountId" buttonText="Copy account id" :tooltip="props.accountId"></CopyButton>
            </p>

            <tabs navClass="tabs list-unstyled">
                <tab title="Settings">
                    <div class="d-flex col-gap-sm flex-wrap align-items-baseline m-lg-b">
                        <h3 class="h5 strong">Company</h3>
                        <router-link :to="{ name: 'SubscriptionEdit', params: { serviceSubscriptionId: state.account.ServiceSubscriptionId } }">
                            {{ state.account.ClientCompany.Name }}
                        </router-link>
                        <button class="btn btn-default btn-sm m-sm-l" type="button" @click="listCompanies">Change</button>
                    </div>

                    <Form
                        v-if="state.formReady"
                        class="fieldset-grid"
                        :data="state.settingsForm.data"
                        @update:isValid="(key, newVal) => (state.settingsForm!.data[key].isValid = newVal)"
                        @update:modelValue="(key, newVal) => (state.settingsForm.data[key].modelValue = newVal)"
                        @onSubmit="saveSettings"></Form>
                </tab>

                <tab title="Sender settings">
                    <Form
                        v-if="state.formReady"
                        class="fieldset-grid"
                        :data="state.senderSettingsForm.data"
                        @update:isValid="(key, newVal) => (state.senderSettingsForm!.data[key].isValid = newVal)"
                        @update:modelValue="(key, newVal) => (state.senderSettingsForm.data[key].modelValue = newVal)"
                        @onSubmit="saveSenderSettings"></Form>
                </tab>

                <tab title="Domain settings">
                    <DomainWrapper :selectedAccount="state.account"></DomainWrapper>
                </tab>

                <tab title="Users">
                    <table class="table table-hover">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Email</th>
                                <th>Name</th>
                                <th>Role</th>
                                <th>Created</th>
                                <th>Last login</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(user, index) in state.users">
                                <td class="autofit text-nowrap">{{ index + 1 }}</td>
                                <td>
                                    <i
                                        class="fa fa-square"
                                        aria-hidden="true"
                                        :class="{ 'status-ok': user.Status == 0, 'status-not-ok': user.Status == 1, 'status-warning': user.Status == 2 }"></i>
                                    <router-link :to="{ name: 'UserEdit', params: { userId: user.UserId } }">
                                        {{ user.Email == null || user.Email == '' ? 'No email' : user.Email }}
                                    </router-link>
                                </td>
                                <td>
                                    {{ user.Name }}
                                </td>
                                <td>
                                    {{ user.RoleName }}
                                </td>
                                <td>
                                    {{ formatDate(user.Created) }}
                                </td>
                                <td>
                                    {{ formatDate(user.LastLogin) }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </tab>

                <tab title="Validate emails">
                    <Loading :show="state.isValidating" loadingStyle="progress-bar"></Loading>

                    <div class="alert alert-info alert-fixed-i m-xs-b" v-if="state.emailValidations.length == 0">
                        <p>
                            <i class="fa fa-info-circle" aria-hidden="true"></i>
                            No emails to validate.
                        </p>
                    </div>

                    <div class="table-responsive" v-else>
                        <table class="table table-hover">
                            <thead>
                                <tr>
                                    <th>Email</th>
                                    <th>Created</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(validation, index) in state.emailValidations">
                                    <td>{{ validation.Email }}</td>
                                    <td>{{ formatDate(validation.Created) }}</td>
                                    <td class="text-right">
                                        <button
                                            title="Validate"
                                            class="btn btn-sm btn-primary"
                                            v-confirm="{ action: () => validate(validation) }"
                                            :disabled="validation.IsValidated">
                                            <span v-if="validation.IsValidated" class="fa fa-check"></span>
                                            <span v-else class="fa fa-thumbs-up"></span>
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </tab>

                <tab title="Delete contacts">
                    <div class="fieldset-grid">
                        <h3 class="m-0-t">Hard delete contacts</h3>

                        <div class="alert alert-info alert-fixed-i">
                            <i class="fa fa-info-circle" aria-hidden="true"></i>
                            <strong>Delete is a three step process:</strong>
                            <ul>
                                <li>Calculate</li>
                                <li>Request delete of contacts</li>
                                <li>Delete each contact</li>
                            </ul>
                            It will start almost immediately, but each contact takes a few seconds to delete so the number of contacts will lower slowly over
                            time.
                        </div>

                        <div class="form-group">
                            <label for="select-delete-type">Select contacts to hard delete:</label>
                            <select id="select-delete-type" class="form-control" v-model="state.contactDeleteType" @change="state.contactDeleteCount = null">
                                <option value="deleted">Deleted contacts</option>
                                <option value="listless">Contacts without list</option>
                                <option value="bounced">Hard bounced contacts</option>
                                <option value="inactive">Unsubscribed contacts</option>
                            </select>
                            <button @click="countDeletion" class="btn btn-primary m-sm-t" :disabled="state.isLoadingContacts">Calculate</button>
                        </div>

                        <Loading :show="state.isLoadingContacts" loadingStyle="progress-bar"></Loading>

                        <div v-if="state.contactDeleteCount != null">
                            <div class="alert alert-danger alert-fixed-i">
                                <p>
                                    <i class="fa fa-warning" aria-hidden="true"></i>
                                    <strong>This action can not be undone</strong>
                                    and will delete
                                    <span>{{ state.contactDeleteCount }}</span>
                                    contacts.
                                </p>
                                <div class="form-group">
                                    <button class="btn btn-danger" v-confirm="{ action: () => deleteContacts() }">Hard Delete</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </tab>
                <tab title="Danger zone">
                    <div class="fieldset-grid">
                        <DeleteAccount :accountId="props.accountId" :isDeleted="state.account.IsDeleted"></DeleteAccount>
                    </div>
                </tab>
            </tabs>
        </div>

        <div v-show="state.currentView === views.companiesList">
            <List
                title="Companies"
                description="Select a company for this account."
                :items="state.companies"
                itemId="ClientCompanyId"
                :filterKeys="['Name']"
                :loading="state.isLoading"
                displayAs="table"
                :tableHeaders="['Name', '']">
                <template #item="{ item }">
                    <td>
                        {{ item.Name }}
                        <span v-show="item.ClientCompanyId === state.account.ClientCompany.ClientCompanyId" class="label label-default m-xxs-l">Current</span>
                    </td>
                    <td class="text-right">
                        <button
                            type="button"
                            class="btn btn-primary btn-sm"
                            @click="updateCurrentCompany(item)"
                            v-show="item.ClientCompanyId !== state.account.ClientCompany.ClientCompanyId">
                            Select
                        </button>
                    </td>
                </template>
            </List>
        </div>
    </template>
</template>

<style scoped>
    .fieldset-grid {
        max-width: 800px;
    }
</style>
